import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { theme } from "../theme";
import { useApp } from "../AppContext";
import NeedsAttention from "../components/NeedsAttention";
import PortfolioChart from "../components/PortfolioChart";
import MetricCard from "../components/MetricCard";
import BiggestDrops from "../components/BiggestDrops";
import ShopSetupNotice from "../components/ShopSetupNotice";

const IkonaPozice = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M7 16v-5M12 16V8M17 16v-3"/></svg>
);
const IkonaPohar = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
);
const IkonaPokles = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M19 12l-7 7-7-7"/></svg>
);

// Jedna hodnota pro vodorovne i svisle mezery mezi kartami.
const MEZERA = 20;

const cislo = (n) => new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 1 }).format(n);

export default function DashboardPage() {
  const {
    token, products, loadingProducts, error,
    groupFiltered, setSelectedProduct, shopMissing,
  } = useApp();
  const navigate = useNavigate();

  const [souhrn, setSouhrn] = useState(null);
  const [nacitam, setNacitam] = useState(true);
  const [dnyPropadu, setDnyPropadu] = useState(30);
  const [dnyGrafu, setDnyGrafu] = useState(30);

  // Metriky nahoře jsou vždy za týden, propady mají vlastní přepínač, takže
  // se souhrn tahá dvakrát — jednou pro hlavičku, jednou pro žebříček.
  const [propady, setPropady] = useState(null);

  const nactiSouhrn = useCallback(async (dny, setter) => {
    try {
      setter(await apiFetch(`/dashboard/summary?days=${dny}`, token));
    } catch (e) {
      setter(null);
    }
  }, [token]);

  useEffect(() => {
    let zivy = true;
    setNacitam(true);
    apiFetch("/dashboard/summary?days=7", token)
      .then(d => { if (zivy) setSouhrn(d); })
      .catch(() => { if (zivy) setSouhrn(null); })
      .finally(() => { if (zivy) setNacitam(false); });
    return () => { zivy = false; };
  }, [token, products.length]);

  useEffect(() => { nactiSouhrn(dnyPropadu, setPropady); }, [dnyPropadu, nactiSouhrn, products.length]);

  const otevriProdukt = (id) => {
    const p = products.find(x => x.id === id);
    if (p) setSelectedProduct(p);
  };

  // ── metriky ────────────────────────────────────────────────────────────────
  const avg = souhrn?.avg_position;
  const delta = avg?.delta;
  const poznamkaPozice = delta == null
    ? "zatím není s čím srovnat"
    : Math.abs(delta) < 0.05
      ? "stejné jako minulý týden"
      : `o ${cislo(Math.abs(delta))} ${delta > 0 ? "lepší" : "horší"} než minulý týden`;

  const poklesy = souhrn?.declines;
  const rozdilPoklesu = poklesy?.delta;
  const poznamkaPoklesu = rozdilPoklesu == null
    ? null
    : rozdilPoklesu === 0
      ? "stejně jako minulý týden"
      : `o ${Math.abs(rozdilPoklesu)} ${rozdilPoklesu > 0 ? "víc" : "míň"} než minulý týden`;

  return (
    <>
      <div className="fadeUp" style={{ marginBottom: MEZERA }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.6px", color: theme.ink, marginBottom: 6 }}>Přehled</h1>
        <p style={{ color: theme.inkSoft, fontSize: 14 }}>
          Sledujete <span style={{ fontVariantNumeric: theme.tnum, fontWeight: 600 }}>{products.length}</span> produkt{products.length === 1 ? "" : products.length < 5 ? "y" : "ů"}
        </p>
      </div>

      {error && (
        <div style={{ background: theme.badSoft, border: `1.5px solid ${theme.badLine}`, borderRadius: theme.rCard, padding: "14px 18px", marginBottom: MEZERA, color: theme.bad, fontSize: 14 }}>
          {error}
        </div>
      )}

      {loadingProducts ? (
        <div style={{ textAlign: "center", padding: 60, color: theme.mut }}>Načítám produkty…</div>
      ) : products.length === 0 ? (
        <>
          {shopMissing && (
            <ShopSetupNotice title="Nevíme, které nabídky jsou vaše">
              Dokud nemáte nastavený e-shop, Rankio nepozná vaši nabídku na Heurece a pozice se neměří.
            </ShopSetupNotice>
          )}
          <div style={{ textAlign: "center", padding: "60px 24px", background: theme.bg, border: `2px dashed ${theme.line}`, borderRadius: theme.rCard }}>
            <p style={{ color: theme.ink, fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Zatím žádné produkty</p>
            <p style={{ color: theme.mut, fontSize: 14, marginBottom: 18 }}>Přidejte svůj první produkt na stránce Produkty.</p>
            <button onClick={() => navigate("/produkty")} style={{ background: theme.blue, border: "none", borderRadius: theme.rBtn, padding: "10px 22px", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Přejít na Produkty
            </button>
          </div>
        </>
      ) : (
        <>
          <div id="tour-health" className="fadeUp" style={{ display: "flex", gap: MEZERA, flexWrap: "wrap", marginBottom: MEZERA }}>
            <MetricCard
              icon={IkonaPozice}
              title="Průměrná pozice"
              value={nacitam ? "…" : (avg?.now != null ? cislo(avg.now) : "—")}
              note={nacitam ? null : poznamkaPozice}
              tone={delta == null || Math.abs(delta) < 0.05 ? "neutral" : delta > 0 ? "ok" : "bad"}
            />
            <MetricCard
              icon={IkonaPohar}
              title="Produkty v TOP 3"
              value={nacitam ? "…" : (souhrn?.top3?.count ?? "—")}
              suffix={souhrn?.top3 ? `z ${souhrn.top3.total}` : null}
            />
            <MetricCard
              icon={IkonaPokles}
              title="Pokles za 7 dní"
              value={nacitam ? "…" : (poklesy?.now ?? "—")}
              note={nacitam ? null : poznamkaPoklesu}
              tone={rozdilPoklesu == null || rozdilPoklesu === 0 ? "neutral" : rozdilPoklesu > 0 ? "bad" : "ok"}
            />
          </div>

          <div id="tour-attention">
            <NeedsAttention products={groupFiltered} onOpen={setSelectedProduct} shopMissing={shopMissing} />
          </div>

          <div id="tour-chart" style={{ display: "flex", gap: MEZERA, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ flex: 1.4, minWidth: 320 }}>
              <PortfolioChart token={token} days={dnyGrafu} onDaysChange={setDnyGrafu} marginBottom={0} shopMissing={shopMissing} key={dnyGrafu} />
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <BiggestDrops
                drops={propady?.biggest_drops || []}
                days={dnyPropadu}
                onDaysChange={setDnyPropadu}
                onOpen={otevriProdukt}
                loading={propady === null}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}
